<script setup lang="ts">
import { watch } from 'vue';
import { useRoute } from 'vue-router';
import sasLogo from '@/images/logos/sas.png';

/// Setup
const route = useRoute();

/// Watchers
// Close any open <details> inside the header and clear nav focus when the route
// changes. Vue Router's client-side navigation preserves DOM state, so the CSS
// :focus-within dropdown and the mobile <details> drawer would otherwise stay
// open after the user clicks a link inside them.
watch(
  () => route.fullPath,
  () => {
    for (const d of document.querySelectorAll<HTMLDetailsElement>('header details[open]')) {
      d.open = false;
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('header')) active.blur();
  },
);
</script>

<template>
  <header class="sticky top-0 z-1000 bg-white shadow">
    <div class="container flex h-80 items-center justify-between xl:h-96">
      <RouterLink to="/">
        <img
          class="h-49 w-auto xl:h-67"
          :src="sasLogo"
          alt="Student Assurance Services Home"
          width="322"
          height="134"
        />
      </RouterLink>
      <nav
        class="flex h-full gap-20 max-md:hidden lg:gap-35 xl:gap-50"
        aria-label="Main navigation"
      >
        <div class="header-menu group">
          <RouterLink to="/k12" aria-haspopup="true" aria-expanded="false">K 12</RouterLink>
          <div role="menu">
            <RouterLink to="/k12" role="menuitem">K12 Students</RouterLink>
            <RouterLink to="/k12/admin/login" role="menuitem">K12 Administrators</RouterLink>
          </div>
        </div>
        <div class="header-menu group">
          <RouterLink to="/college" aria-haspopup="true" aria-expanded="false">College</RouterLink>
          <div role="menu">
            <RouterLink to="/college" role="menuitem">College Students</RouterLink>
            <RouterLink to="/college/admin/login" role="menuitem"
              >College Administrators</RouterLink
            >
          </div>
        </div>
        <div class="header-menu group">
          <RouterLink to="/specialrisk">Special Risk</RouterLink>
        </div>
        <div class="header-menu group">
          <RouterLink to="/aboutus" aria-haspopup="true" aria-expanded="false">About Us</RouterLink>
          <div role="menu">
            <RouterLink to="/aboutus/allproducts" role="menuitem"
              >Product &amp; Services</RouterLink
            >
            <RouterLink to="/aboutus/requestinfo" role="menuitem">Request Information</RouterLink>
            <RouterLink to="/aboutus/faq" role="menuitem">FAQs</RouterLink>
          </div>
        </div>
        <div class="header-menu group">
          <RouterLink to="/providers">Providers</RouterLink>
        </div>
      </nav>
      <RouterLink class="header-link max-md:hidden" to="/contactus">Contact Us</RouterLink>
      <details class="group/nav md:hidden">
        <summary
          class="flex h-40 w-40 cursor-pointer list-none flex-col items-center justify-center gap-5 [&::-webkit-details-marker]:hidden"
          aria-label="Toggle navigation menu"
        >
          <span
            class="h-2.5 w-24 origin-center rounded-full bg-gray-800 transition-all duration-300 group-open/nav:translate-y-7.5 group-open/nav:rotate-45"
          />
          <span
            class="h-2.5 w-24 rounded-full bg-gray-800 transition-opacity duration-300 group-open/nav:opacity-0"
          />
          <span
            class="h-2.5 w-24 origin-center rounded-full bg-gray-800 transition-all duration-300 group-open/nav:-translate-y-7.5 group-open/nav:-rotate-45"
          />
        </summary>
        <nav
          class="fixed inset-x-0 top-80 bottom-0 z-1000 flex flex-col overflow-y-auto bg-white shadow-lg *:container"
          aria-label="Mobile navigation"
        >
          <details name="mobile-section" class="header-menu-mobile">
            <summary><RouterLink to="/k12">K 12</RouterLink></summary>
            <div>
              <RouterLink to="/k12">K12 Students</RouterLink>
              <RouterLink to="/k12/admin/login">K12 Administrators</RouterLink>
            </div>
          </details>
          <details name="mobile-section" class="header-menu-mobile">
            <summary><RouterLink to="/college">College</RouterLink></summary>
            <div>
              <RouterLink to="/college">College Students</RouterLink>
              <RouterLink to="/college/admin/login">College Administrators</RouterLink>
            </div>
          </details>
          <RouterLink class="header-menu-mobile" to="/specialrisk">Special Risk</RouterLink>
          <details name="mobile-section" class="header-menu-mobile">
            <summary><RouterLink to="/aboutus">About Us</RouterLink></summary>
            <div>
              <RouterLink to="/aboutus/allproducts">Product &amp; Services</RouterLink>
              <RouterLink to="/aboutus/requestinfo">Request Information</RouterLink>
              <RouterLink to="/aboutus/faq">FAQs</RouterLink>
            </div>
          </details>
          <RouterLink class="header-menu-mobile" to="/providers">Providers</RouterLink>
          <RouterLink class="header-menu-mobile" to="/contactus">Contact Us</RouterLink>
        </nav>
      </details>
    </div>
  </header>
</template>
